// This file is meant to hold utility methods that don't have any Blink-specific logic,
// so they can be used anywhere

import {isMobileDevice} from "../stem-core/base/Device";
import {MOBILE_DEVICE_SCREEN_WIDTH_LIMIT, SMALL_SCREEN_WIDTH_LIMIT} from "./Constants";

export * from "../stem-core/base/Utils";

// BlinkGlobal can be used to prevent circular dependencies, and where we don't want to pollute window/self.
export const BlinkGlobal: {
    iFrameState?: {
        screen?: {
            width: number;
            isEmulatorScreen?: boolean;
        };
    };
    appType?: "widget" | "panel"; // TODO IFRAME_APP_TYPE
    //userService?: UserService;
    //userId: number | string; // TODO define exactly
} | any = {};

export function getUrlQueryString(): string {
    return window.location.search;
}

function parseQueryParam(value: string): any {
    try {
        return JSON.parse(value);
    } catch {
        try {
            return JSON.parse(decodeURIComponent(value));
        } catch {
            try {
                return decodeURIComponent(value);
            } catch {
                return null;
            }
        }
    }
}

export function getQueryParam(param: string): any {
    const urlSearch = new URLSearchParams(getUrlQueryString());
    return parseQueryParam(urlSearch.get(param));
}

export function formatQueryParam(param: any): string {
    try {
        return encodeURIComponent(JSON.stringify(param));
    } catch (e1) {
        try {
            return encodeURIComponent(param);
        } catch (e2) {
            return "";
        }
    }
}

export function generateUniqueId(): string {
    // TODO @cleanup ugh
    // generate UUID v4
    const seq = Date.now().toString(16) + (Math.random() * 1e18 | 0).toString(16) + (Math.random() * 1e18 | 0).toString(16);

    return [seq.substring(0, 8), seq.substring(8, 12), `4${seq.substring(12, 15)}`, `8${seq.substring(15, 18)}`, seq.substring(18, 30)].join("-");
}

export function getWindowWidth(): number {
    if (BlinkGlobal.iFrameState?.screen && isMobileDevice()) {
        return BlinkGlobal.iFrameState.screen.width;
    }
    if (window.screen && isMobileDevice()) {
        return window.screen.width;
    }
    return window.innerWidth;
}

export function getWindowHeight(): number {
    return document.documentElement.clientHeight;
}

export function isSmallScreen(): boolean {
    if (BlinkGlobal.iFrameState?.screen?.isEmulatorScreen) {
        return BlinkGlobal.iFrameState.screen.width <= MOBILE_DEVICE_SCREEN_WIDTH_LIMIT;
    }
    if (window.screen && isMobileDevice()) {
        return getWindowWidth() <= MOBILE_DEVICE_SCREEN_WIDTH_LIMIT;
    }
    return getWindowWidth() <= SMALL_SCREEN_WIDTH_LIMIT;
}

export function getPlatform(): string {
    return navigator?.userAgentData?.platform || navigator?.platform || "unknown";
}

export function isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

interface DocumentHiddenInfo {
    hidden: boolean;
    event?: string;
}

export function getDocumentHiddenInfo(): DocumentHiddenInfo {
    if (document.hidden) {
        return {
            hidden: true,
            event: "visibilitychange"
        };
    }

    if (document.msHidden) {
        return {
            hidden: true,
            event: "msvisibilitychange"
        };
    }

    if (document.webkitHidden) {
        return {
            hidden: true,
            event: "webkitvisibilitychange"
        };
    }

    return {hidden: false};
}

interface RemoveHandlerResult {
    removeHandler: (() => void) | null;
}

export function executeWhenDocumentVisible(callback: () => void): RemoveHandlerResult {
    const hiddenInfo = getDocumentHiddenInfo();
    if (!hiddenInfo.hidden) {
        callback();
        return {removeHandler: null};
    }
    const handlerFunction = () => {
        setTimeout(callback, 300);
        removeHandler();
    };
    const removeHandler = () => document.removeEventListener(hiddenInfo.event!, handlerFunction);
    document.addEventListener(hiddenInfo.event!, handlerFunction);
    return {removeHandler};
}

export function openWindowWithOpener(url = "", target = "_blank"): Window | null {
    return window.open(url, target, "opener");
}

interface ParsedElement {
    tag: string;
    children: (string | ParsedElement)[];
    [key: string]: any;
}

export function convertHTMLStringToJSON(value: string, optionsWrapper: (x: any) => any = (x => x)): (string | ParsedElement)[] {
    const parser = new DOMParser();
    const document = parser.parseFromString(`<body>${value}</body>`, "text/xml");
    const parseElements = (root: Node): string | ParsedElement | null => {
        if (root.nodeType === Node.TEXT_NODE) {
            const value = (root as Text).wholeText;
            let trimmedValue = value.trim();
            if (!trimmedValue) {
                return null;
            }
            // TODO why? it looks like if there were spaces at the end, we want to combine into one at most at each end
            if (trimmedValue[0] !== value[0]) {
                trimmedValue = " " + trimmedValue;
            }
            if (trimmedValue[trimmedValue.length - 1] !== value[value.length - 1]) {
                trimmedValue += " ";
            }
            return trimmedValue;
        }

        if (root.nodeType !== Node.ELEMENT_NODE) {
            return null;
        }

        const element = root as Element;
        let options: Record<string, string | null> = {};
        element.getAttributeNames().forEach(name => {
            options[name] = element.getAttribute(name);
        });
        return optionsWrapper({
            tag: element.tagName,
            children: Array.from(element.childNodes).map(parseElements).filter(Boolean),
            ...options,
        });
    };

    // First child of the document will be the body.
    return Array.from(document.childNodes[0].childNodes).map(parseElements).filter(Boolean) as (string | ParsedElement)[];
}
